import { Field, Formik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { PriorityInfo } from '../../services/buildfire/rdb/cases';
import Form from '../common/Form';
import {
  IonButton,
  IonDatetime,
  IonDatetimeButton,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonToggle,
} from '@ionic/react';
import styled from 'styled-components';
import moment from 'moment';
import AlertBox from '../common/AlertBox';
import classNames from 'classnames';
import { uppercaseFirstLetter } from '../../util/string';
import FormButtonContainer from '../common/FormButtonContainer';

const StyledForm = styled(Form)`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const FormErrorContainer = styled.div`
  padding: 16px;
`;

const PriceInput = styled(IonInput)`
  text-align: right;
`;

const TimeModal = styled(IonModal)`
  --backdrop-opacity: var(--ion-backdrop-opacity, 0.32) !important;
`;

const FormContent = styled.div``;

interface PriorityCaseSettingsFormProps {
  initialValues: PriorityInfo;
  onSubmit: (values: PriorityInfo) => Promise<void>;
  onCancel: () => void;
}

const PriorityCaseSettingsForm: React.FC<PriorityCaseSettingsFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  return (
    <Formik
      initialValues={{
        enabled: initialValues.enabled || false,
        businessStart: initialValues.businessStart || '08:00',
        businessEnd: initialValues.businessEnd || '17:00',
        price: (initialValues.price / 100).toFixed(2), // Convert cents to dollars
      }}
      onSubmit={async (values) => {
        const updatedValues: PriorityInfo = {
          ...values,
          price: parseFloat(values.price) * 100, // Convert dollars back to cents
        };
        await onSubmit(updatedValues);
      }}
      validateOnMount
      validationSchema={Yup.object({
        enabled: Yup.boolean().required('Enabled is required'),
        businessStart: Yup.string().required('Business start time is required'),
        businessEnd: Yup.string()
          .required('Business end time is required')
          .test(
            'is-greater',
            'End time must be after start time',
            function (value) {
              const { businessStart } = this.parent;
              return moment(value, 'HH:mm').isAfter(
                moment(businessStart, 'HH:mm')
              );
            }
          ),
        price: Yup.string().required('Price is required'),
      })}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        handleChange,
        handleBlur,
        handleSubmit,
        isValid,
        dirty,
      }) => (
        <StyledForm onSubmit={handleSubmit}>
          <FormContent>
            <IonItem>
              <IonToggle
                name="enabled"
                checked={values.enabled}
                onIonChange={(e) => setFieldValue('enabled', e.detail.checked)}
              >
                Enabled
              </IonToggle>
            </IonItem>

            <IonItem>
              <IonLabel>Business Start Time (CT)</IonLabel>

              <IonDatetimeButton datetime="businessStartDateTime" />
            </IonItem>
            <IonItem>
              <IonLabel>Business End Time (CT)</IonLabel>
              <IonDatetimeButton datetime="businessEndDateTime" />
            </IonItem>

            <IonItem>
              <PriceInput
                className={classNames({
                  ['ion-invalid']: errors.price,
                  ['ion-touched']: touched.price,
                })}
                label="Price"
                name="price"
                type="number"
                required
                pattern="^\d+(\.\d{1,2})?$"
                value={values.price}
                onIonInput={(e) => {
                  // const inputValue = e.detail.value || '';
                  // const numericValue = parseFloat(
                  //   inputValue.replace(/[^0-9.]/g, '')
                  // );
                  // console.log('Numeric Value:', numericValue);
                  // setFieldValue(
                  //   'price',
                  //   isNaN(numericValue) ? 0 : numericValue * 100
                  // );
                  setFieldValue('price', e.detail.value);
                }}
                onIonBlur={(e) => {
                  setFieldTouched('price', true);
                }}
                errorText={uppercaseFirstLetter(errors.price as string)}
              />
            </IonItem>

            <FormErrorContainer>
              {errors.businessStart && touched.businessStart && (
                <AlertBox type="error" text={errors.businessStart} />
              )}
              {errors.businessEnd && touched.businessEnd && (
                <AlertBox type="error" text={errors.businessEnd} />
              )}
            </FormErrorContainer>

            <TimeModal keepContentsMounted={true} showBackdrop>
              <IonDatetime
                id="businessStartDateTime"
                presentation="time"
                name="businessStart"
                value={values.businessStart}
                onIonChange={(e) => {
                  setFieldValue('businessStart', e.detail.value);
                  setFieldTouched('businessStart', true);
                }}
              />
            </TimeModal>
            <TimeModal keepContentsMounted={true} showBackdrop>
              <IonDatetime
                id="businessEndDateTime"
                presentation="time"
                name="businessEnd"
                value={values.businessEnd}
                onIonChange={(e) => {
                  setFieldValue('businessEnd', e.detail.value);
                  setFieldTouched('businessEnd', true);
                }}
              />
            </TimeModal>
          </FormContent>

          <FormButtonContainer>
            <IonButton onClick={onCancel} fill="clear">
              Cancel
            </IonButton>
            <IonButton
              type="submit"
              disabled={isSubmitting || !isValid || !dirty}
            >
              Save
            </IonButton>
          </FormButtonContainer>

          {/* <Field
            name="businessStart"
            type="number"
            placeholder="Business Start Time (0-23)"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.businessStart}
            className={
              touched.businessStart && errors.businessStart ? 'error' : ''
            }
            errorText={errors.businessStart}
          />
          <Field
            name="businessEnd"
            type="number"
            placeholder="Business Start Time (0-23)"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.businessStart}
            className={
              touched.businessStart && errors.businessStart ? 'error' : ''
            }
            errorText={errors.businessStart}
          /> */}
        </StyledForm>
      )}
    </Formik>
  );
};

export default PriorityCaseSettingsForm;
